import { WebhookEventType } from "@/enums/settings";
import { TwilioBusinessType } from "@/enums/twilio";
import { WebhookSecretLocation } from "@/types/workflows";

/**
 * The secret Galactus sends with each delivery so a workflow's Start node accepts it (GAIL-1983).
 * The stored URL never carries it, even for `in: query`.
 */
export type WebhookTriggerSecret = {
  value: string;
  in: WebhookSecretLocation;
  name: string;
};

export type WebhookRequest = {
  url: string;
  event: WebhookEventType;
  triggerSecret?: WebhookTriggerSecret | null;
};

export type Webhook = {
  id: string;
  isArchived: boolean;
  signingEnabled: boolean;
} & WebhookRequest;

/** Only the create and rotate responses carry the secret; GET never returns it. */
export type WebhookWithSecret = Webhook & { signingSecret: string };

/**
 * POST /api/settings/agency/webhooks and PUT /[webhookId]. `webhooks` is null
 * when the caller lacks Settings Read or the follow-up list failed; `webhook`
 * is then the created row so the one-time `signingSecret` can still be shown.
 */
export type WebhookSaveResponse =
  | { webhooks: Webhook[]; signingSecret: string }
  | { webhooks: null; signingSecret: string; webhook: Webhook };

export type VerifyPhoneRequest = {
  businessIdType: "EIN" | "CBN";
  businessId: string;
  businessLegalName: string;
  businessType: TwilioBusinessType;
};

export type VerifyPhoneResponse = {
  success: boolean;
  messageKey?: string;
  missingFields?: string[];
};

// ============================================
// Calendly API Types (for direct Calendly API calls)
// ============================================

export interface CalendlyUserResponse {
  resource: {
    uri: string;
    name: string;
    email: string;
    scheduling_url: string;
    timezone: string;
    current_organization: string;
  };
}

export interface CalendlyEventTypesResponse {
  collection: CalendlyEventType[];
  pagination: { count: number };
}

export type CalendlyEventType = {
  uri: string; // Use this for allowedEventTypeUris
  name: string;
  active: boolean;
  slug: string;
  scheduling_url: string;
  duration: number; // minutes
  kind: "solo" | "group";
  description_plain: string | null;
  locations: { kind: string; location?: string }[] | null;
};

export type CalendlyUser = {
  uri: string;
  name: string;
  email: string;
  timezone: string;
  current_organization: string;
  scheduling_url: string;
};

// ============================================
// Raccoon API Types (for saving config)
// ============================================

export interface CalendlySettings {
  allowedEventTypeUris: string[]; // Array of event type URIs
}

export interface CalendlyIntegrationRequest {
  name: "calendly";
  credentials: {
    type: "api_key";
    key: string; // Calendly PAT
  };
  settings: CalendlySettings;
}

// Response from Raccoon when fetching integration
export interface CalendlyIntegrationResponse {
  id: string;
  name: "calendly";
  credentials: {
    type: "api_key";
    key: string;
  };
  settings: CalendlySettings;
}

// Generic Raccoon integration stored in the database
export interface RaccoonIntegration {
  id: string;
  name: string;
  credentials: {
    type: string;
    key?: string;
  };
  settings?: Record<string, unknown>;
}

// Response wrapper from Raccoon API
export interface RaccoonApiResponse<T = unknown> {
  data?: T;
  errors?: string[];
}

export interface SettingsPageProps {
  show: boolean;
}

export enum ConversationTTL {
  OneHour = "one_hour",
  OneDay = "one_day",
  AllTime = "all_time",
}

export interface ConversationTTLResponse {
  smsConversationTtl: ConversationTTL;
  whatsappTtl: ConversationTTL;
  // Optional while the backend rolls out chat TTL support — defaults to AllTime
  // when the API does not yet return this field.
  chatConversationTtl?: ConversationTTL;
  /**
   * Backend validation errors. Present only on a rejected PATCH when
   * `apiFetch` is called with `alwaysReturn: true` — in that case the
   * other fields may be empty and callers must check this before
   * treating the response as success. Matches the same envelope as
   * `AssistantIdleReminderSettingsResponse` /
   * `AssistantDataExtractionSettingsResponse` /
   * `ConversationDetails`.
   */
  errors?: Array<{ detail?: string }>;
}

export interface UpdateConversationTTLPayload {
  smsConversationTtl: ConversationTTL;
  whatsappTtl: ConversationTTL;
  // Optional while the backend rolls out chat TTL support. Once the backend
  // accepts this field, callers should always include it.
  chatConversationTtl?: ConversationTTL;
}

export interface OrganizationSecuritySettings {
  password_expiration_days: number | null;
  idle_timeout_minutes: number;
  absolute_timeout_minutes: number;
  allowed_ip_addresses: string[] | null;
}

// ============================================
// SMS Settings (Galactus /.internal/sms_settings)
// ============================================

/**
 * Mirrors the Galactus SmsSettingsDtos polymorphic hierarchy — the concrete
 * type is selected by the `provider` discriminator. Secret fields
 * (`authToken`, `apiKey`, `apiSecret`) are always null in responses and are
 * preserved server-side when left null on update.
 */
export interface TwilioSmsProviderSettings {
  provider: "twilio";
  accountSid?: string | null;
  authToken?: string | null;
  phoneNumber?: string | null;
  messagingServiceSid?: string | null;
}

export interface DirectoSmsProviderSettings {
  provider: "directo";
  fromPhoneNumber?: string | null;
}

export interface QuicklySmsProviderSettings {
  provider: "quickly";
  url?: string | null;
  apiKey?: string | null;
  apiSecret?: string | null;
}

export type SmsProviderSettings =
  | TwilioSmsProviderSettings
  | DirectoSmsProviderSettings
  | QuicklySmsProviderSettings;

export type SmsProviderType = SmsProviderSettings["provider"];

export interface SmsIdleReminderSettings {
  enabled: boolean;
  /** TimeSpan string ("hh:mm:ss"). Not editable from this UI. */
  reminderAfter?: string | null;
}

export interface SmsSettings {
  id: string;
  provider: SmsProviderSettings;
  smsConversationTtl: ConversationTTL;
  smsIdleReminder: SmsIdleReminderSettings;
}

export type SmsSettingsRequest = Omit<SmsSettings, "id">;

// ============================================
// Cal.com API Types (for Raccoon integration)
// ============================================

/** Location types that Galactus understands (maps to CalDotComLocationType enum). */
export type GalactusLocationType = "none" | "phone" | "inPerson" | "link" | "googleMeet" | "microsoftTeams";

// Event type info passed to Galactus for tool seeding
export interface CalDotComEventType {
  eventTypeId: number;
  title: string;
  lengthInMinutes: number;
  maxBookingHorizonDays?: number;
  locationType: GalactusLocationType;
  locationValue?: string; // address or URL if applicable (not used for video conferencing - links are auto-generated)
}

// Settings stored in Raccoon for Cal.com integration
export interface CalDotComSettings {
  teamId: string; // Cal.com team ID
}

// Request to create/update Cal.com integration in Raccoon
export interface CalDotComIntegrationRequest {
  name: "cal-dot-com";
  credentials: {
    type: "no_auth"; // Cal.com uses no auth credentials
  };
  settings: CalDotComSettings;
}

// Response from Raccoon when fetching Cal.com integration
export interface CalDotComIntegrationResponse {
  id: string;
  name: "cal-dot-com";
  credentials: {
    type: "no_auth";
  };
  settings: CalDotComSettings;
}

// Payload for syncing event types to Galactus
export interface CalDotComEventTypesPayload {
  eventTypes: CalDotComEventType[];
}
